import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import TrialBanner from './components/TrialBanner';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import EducationHub from './pages/EducationHub';
import ArticleDetail from './pages/ArticleDetail';
import NewsPage from './pages/NewsPage';
import NewsDetail from './pages/NewsDetail';
import MacroBriefList from './pages/MacroBriefList';
import MacroBriefDetail from './pages/MacroBriefDetail';
import ForumPage from './pages/ForumPage';
import PostDetail from './pages/PostDetail';
import CreatePost from './pages/CreatePost';
import AboutPage from './pages/AboutPage';
import AdminDashboard from './pages/AdminDashboard';
import UserProfile from './pages/UserProfile';
import DisclaimerPage from './pages/DisclaimerPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AiAdvisor from './pages/AiAdvisor';
import CheckoutSuccess from './pages/CheckoutSuccess';
import IntelligenceFeed from './pages/IntelligenceFeed';
import TrackRecord from './pages/TrackRecord';
import PatternLibrary from './pages/PatternLibrary';
import TradingArena from './pages/TradingArena';
import InstitutionalAnalysis from './pages/InstitutionalAnalysis';
import LiveChart from './pages/LiveChart';
import ResearchHub from './pages/ResearchHub';
import CryptoProfile from './pages/CryptoProfile';
import StartHere from './pages/StartHere';
import ChartReader from './pages/ChartReader';
import WhaleWatch from './pages/WhaleWatch';
import SignalEngine from './pages/SignalEngine';
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';

// Auth Context
import { AuthProvider } from './context/AuthContext';
import { usePixelPageView } from './hooks/usePixelPageView';

function AppRouter() {
  const location = useLocation();
  usePixelPageView();
  
  // CRITICAL: Check URL fragment (not query params) for session_id
  // This must happen during render, not in useEffect, to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/education" element={<EducationHub />} />
      <Route path="/education/:articleId" element={<ArticleDetail />} />
      <Route path="/news" element={<NewsPage />} />
      <Route path="/news/:newsId" element={<NewsDetail />} />
      <Route path="/macro-brief" element={<MacroBriefList />} />
      <Route path="/macro-brief/:slug" element={<MacroBriefDetail />} />
      <Route path="/live-markets" element={<Navigate to="/live-chart" replace />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/ai-advisor" element={<AiAdvisor />} />
      <Route path="/intelligence" element={<IntelligenceFeed />} />
      <Route path="/track-record" element={<TrackRecord />} />
      <Route path="/patterns" element={<PatternLibrary />} />
      <Route path="/trading" element={<TradingArena />} />
      <Route path="/analysis" element={<InstitutionalAnalysis />} />
      <Route path="/live-chart" element={<LiveChart />} />
      <Route path="/research" element={<ResearchHub />} />
      <Route path="/research/:slug" element={<CryptoProfile />} />
      <Route path="/start-here" element={<StartHere />} />
      <Route path="/chart-reader" element={<ChartReader />} />
      <Route path="/whale-watch" element={<WhaleWatch />} />
      <Route path="/signal-engine" element={<SignalEngine />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/disclaimer" element={<DisclaimerPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      
      {/* Protected Routes */}
      <Route path="/forum" element={<ProtectedRoute><ForumPage /></ProtectedRoute>} />
      <Route path="/forum/:postId" element={<ProtectedRoute><PostDetail /></ProtectedRoute>} />
      <Route path="/forum/create" element={<ProtectedRoute><CreatePost /></ProtectedRoute>} />
      <Route path="/profile/:userId" element={<UserProfile />} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <TrialBanner />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
